import type { ValueOf } from './util/typeUtils';

export const APPLICANT_FLAGS = {
    // NOTE is only set if information about the contact information is missing
    CONTACT_INFORMATION: 'contact_information',
    // NOTE is only set if information about the job situation is missing
    JOB_SITUATION: 'job_situation',
    JOBLESS: 'jobless',
    // NOTE I know the name LEAD_WITH_REAL_ESTATE is misleading in the APPLICANT_FLAGS, but it is used in the codebase
    LEAD_WITH_REAL_ESTATE: 'lead_with_real_estate',
    // NOTE is only set if information about the living situation is missing
    LIVING_SITUATION: 'living_situation',
    LOW_INCOME: 'low_income',
    NO_INCOME: 'no_income',
    NOT_DE: 'not_DE',
    // NOTE is only set if information about the personal situation is missing
    PERSONAL_SITUATION: 'personal_situation',
    RENT_FREE: 'rent_free',
    SCHUFA_CONTAINS_REAL_ESTATE_LOAN: 'schufa_contains_real_estate_loan',
    SCHUFA_ERROR: 'schufa_error',
    SCHUFA_NEGATIVE: 'schufa_negative',
    SCHUFA_NOT_REQUESTED: 'schufa_not_requested',
    SCHUFA_SCORE: 'schufa_score',
    SELF_EMPLOYED: 'self_employed',
    STUDENT: 'student',
    TOO_YOUNG: 'too_young',
    TRAINEE: 'trainee',
} as const;
export type APPLICANT_FLAG = ValueOf<typeof APPLICANT_FLAGS>;

export const LEAD_FLAGS = {
    ...APPLICANT_FLAGS,
    AFFILIATED_PARTNER_LEAD: 'affiliated_partner_lead',
    // NOTE is only set if information about the bank details is missing
    BANK_DETAILS: 'bank_details',
    // NOTE is only set if information about the co applicant is missing
    CO_APPLICANT: 'co_applicant',
    COMPLETE: 'complete',
    COOPERATION_PARTNER_LEAD: 'cooperation_partner_lead',
    LOW_INCOME_2APPLICANT: 'low_income_2applicant',
    NOT_PARTNER_LEAD: 'not_partner_lead',
    PK: 'PK',
} as const;
export type LEAD_FLAG = ValueOf<typeof LEAD_FLAGS>;

const LEAD_FLAGS_TRIGGER_ROOT = {
    ATTRIBUTION_DATA_ADDED: 'ATTRIBUTION_DATA_ADDED',
    ATTRIBUTION_DATA_MISSING_INFO: 'ATTRIBUTION_DATA_MISSING_INFO',
    BANK_CHANGED: 'BANK_CHANGED',
    CO_APPLICANT_EMAIL_VERIFIED: 'CO_APPLICANT_EMAIL_VERIFIED',
    PROCHECK: 'PROCHECK',
    RECALCULATE_LEAD_FLAGS: 'RECALCULATE_LEAD_FLAGS',
} as const;

export const LEAD_FLAGS_TRIGGER = {
    applicant1: {
        ADDRESS_CHANGED: 'APPLICANT1_ADDRESS_CHANGED',
        BIRTH_DATA_UPDATED: 'APPLICANT1_BIRTH_DATA_UPDATED',
        CREDIT_RATING_FAILED: 'APPLICANT1_CREDIT_RATING_FAILED',
        CREDIT_RATING_REJECTED: 'APPLICANT1_CREDIT_RATING_REJECTED',
        EMPLOYMENT_CHANGED: 'APPLICANT1_EMPLOYMENT_CHANGED',
        LIVING_SITUATION_CHANGED: 'APPLICANT1_LIVING_SITUATION_CHANGED',
        MARITAL_STATUS_CHANGED: 'APPLICANT1_MARITAL_STATUS_CHANGED',
        NEGATIVE_CREDIT_RATING_RECEIVED: 'APPLICANT1_NEGATIVE_CREDIT_RATING_RECEIVED',
        POSITIVE_CREDIT_RATING_RECEIVED: 'APPLICANT1_POSITIVE_CREDIT_RATING_RECEIVED',
    },
    applicant2: {
        ADDRESS_CHANGED: 'APPLICANT2_ADDRESS_CHANGED',
        BIRTH_DATA_UPDATED: 'APPLICANT2_BIRTH_DATA_UPDATED',
        CO_APPLICANT_CENSORED: 'APPLICANT2_CO_APPLICANT_CENSORED',
        CREDIT_RATING_FAILED: 'APPLICANT2_CREDIT_RATING_FAILED',
        CREDIT_RATING_REJECTED: 'APPLICANT2_CREDIT_RATING_REJECTED',
        EMPLOYMENT_CHANGED: 'APPLICANT2_EMPLOYMENT_CHANGED',
        LIVING_SITUATION_CHANGED: 'APPLICANT2_LIVING_SITUATION_CHANGED',
        MARITAL_STATUS_CHANGED: 'APPLICANT2_MARITAL_STATUS_CHANGED',
        NEGATIVE_CREDIT_RATING_RECEIVED: 'APPLICANT2_NEGATIVE_CREDIT_RATING_RECEIVED',
        POSITIVE_CREDIT_RATING_RECEIVED: 'APPLICANT2_POSITIVE_CREDIT_RATING_RECEIVED',
    },
    ...LEAD_FLAGS_TRIGGER_ROOT,
} as const;

export type LEAD_FLAG_TRIGGER =
    | ValueOf<(typeof LEAD_FLAGS_TRIGGER)['applicant1']>
    | ValueOf<(typeof LEAD_FLAGS_TRIGGER)['applicant2']>
    | Extract<ValueOf<typeof LEAD_FLAGS_TRIGGER>, string>;

export const ALL_LEAD_FLAG_TRIGGER_VALUES = [
    ...Object.values(LEAD_FLAGS_TRIGGER.applicant1),
    ...Object.values(LEAD_FLAGS_TRIGGER.applicant2),
    ...Object.values(LEAD_FLAGS_TRIGGER_ROOT),
] as unknown as [LEAD_FLAG_TRIGGER, ...LEAD_FLAG_TRIGGER[]];

export const LEAD_FLAGS_REQUESTED_REASONS = {
    APPLICANT_ADDRESS_CHANGED: 'APPLICANT_ADDRESS_CHANGED',
    APPLICANT_BANK_CHANGED: 'APPLICANT_BANK_CHANGED',
    APPLICANT_BIRTH_DATA_UPDATED: 'APPLICANT_BIRTH_DATA_UPDATED',
    APPLICANT_CREDIT_RATING_FAILED: 'APPLICANT_CREDIT_RATING_FAILED',
    APPLICANT_CREDIT_RATING_MERGED: 'APPLICANT_CREDIT_RATING_MERGED',
    APPLICANT_CREDIT_RATING_REJECTED: 'APPLICANT_CREDIT_RATING_REJECTED',
    APPLICANT_EMPLOYMENT_CHANGED: 'APPLICANT_EMPLOYMENT_CHANGED',
    APPLICANT_LIVING_SITUATION_CHANGED: 'APPLICANT_LIVING_SITUATION_CHANGED',
    APPLICANT_MARITAL_STATUS_CHANGED: 'APPLICANT_MARITAL_STATUS_CHANGED',
    APPLICANT_NEGATIVE_CREDIT_RATING_RECEIVED: 'APPLICANT_NEGATIVE_CREDIT_RATING_RECEIVED',
    APPLICANT_POSITIVE_CREDIT_RATING_RECEIVED: 'APPLICANT_POSITIVE_CREDIT_RATING_RECEIVED',
    ATTRIBUTION_DATA_ADDED: 'ATTRIBUTION_DATA_ADDED',
    ATTRIBUTION_DATA_MISSING_INFO: 'ATTRIBUTION_DATA_MISSING_INFO',
    CO_APPLICANT_CENSORED: 'CO_APPLICANT_CENSORED',
    RECALCULATE_LEAD_FLAGS: 'RECALCULATE_LEAD_FLAGS',
} as const;
export type LEAD_FLAGS_REQUESTED_REASON = ValueOf<typeof LEAD_FLAGS_REQUESTED_REASONS>;
